/**
 * CONFIGURATION CENTRALISÉE DES CALCULS DE PRIX - KRILLOU
 * =========================================================
 * 
 * Tous les paramètres de calcul de prix, frais, taxes sont centralisés ici
 * pour éviter les incohérences et faciliter les modifications
 */

/**
 * Configuration des frais et taxes
 */
export const PRICING_CONFIG = {
  // Frais de service (commission Krillou)
  SERVICE_FEE_PERCENT: 8, // 8% sur le montant de location
  SERVICE_FEE_MIN: 5,     // Frais minimum en EUR
  SERVICE_FEE_MAX: 500,   // Frais maximum en EUR

  // Taxes (TVA)
  TAX_PERCENT: 20,        // TVA 20% (standard français)
  TAX_APPLIED_ON: 'service_fee', // 'location', 'service_fee', 'both'

  // Limites de location
  MIN_RENTAL_DAYS: 1,
  MAX_RENTAL_DAYS: 365,
  MIN_RENTAL_HOURS: 4,    // Location minimum 4h pour tarif horaire
  
  // Arrondis
  ROUND_TO_CENTS: true,   // Arrondir au centime
  ROUND_METHOD: 'ceil',   // 'floor', 'ceil', 'round'

  // Caution
  DEPOSIT_CALCULATION: 'listing', // 'listing', 'percentage', 'fixed'
  DEFAULT_DEPOSIT_PERCENT: 30,    // 30% du prix de location si pas défini
  MIN_DEPOSIT: 50,               // Caution minimum
  MAX_DEPOSIT: 2000,            // Caution maximum

  // Remises automatiques
  WEEKLY_DISCOUNT_PERCENT: 5,   // 5% de remise sur tarif hebdo vs journalier
  MONTHLY_DISCOUNT_PERCENT: 10, // 10% de remise sur tarif mensuel vs journalier

  // Autres frais
  PAYMENT_PROCESSING_FEE_PERCENT: 0, // Pas de frais de paiement supplémentaire
  CANCELLATION_FEE_PERCENT: 50,      // 50% en cas d'annulation tardive
};

/**
 * Configuration par région/pays
 */
export const REGIONAL_CONFIG = {
  'FR': {
    TAX_PERCENT: 20,
    TAX_NAME: 'TVA',
    CURRENCY: 'EUR'
  },
  'DZ': {
    TAX_PERCENT: 19,
    TAX_NAME: 'TVA',
    CURRENCY: 'DZD'
  },
  'MA': {
    TAX_PERCENT: 20,
    TAX_NAME: 'TVA',
    CURRENCY: 'MAD'
  },
  'TN': {
    TAX_PERCENT: 19,
    TAX_NAME: 'TVA',
    CURRENCY: 'TND'
  }
};

/**
 * Obtenir la configuration pour une région
 */
export const getRegionalConfig = (countryCode = 'FR') => {
  return REGIONAL_CONFIG[countryCode] || REGIONAL_CONFIG['FR'];
};

/**
 * Calcul des frais de service avec limites
 */
export const calculateServiceFee = (baseAmount, config = PRICING_CONFIG) => {
  const feeAmount = (baseAmount * config.SERVICE_FEE_PERCENT) / 100;
  
  // Appliquer les limites min/max
  const finalFee = Math.max(
    config.SERVICE_FEE_MIN,
    Math.min(config.SERVICE_FEE_MAX, feeAmount)
  );
  
  return {
    amount: finalFee,
    percent: (finalFee / baseAmount) * 100,
    isMinApplied: feeAmount < config.SERVICE_FEE_MIN,
    isMaxApplied: feeAmount > config.SERVICE_FEE_MAX
  };
};

/**
 * Calcul de la TVA
 */
export const calculateTax = (baseAmount, serviceFee, config = PRICING_CONFIG, regionalConfig = null) => {
  const taxPercent = regionalConfig?.TAX_PERCENT || config.TAX_PERCENT;
  
  let taxableAmount = 0;
  
  switch (config.TAX_APPLIED_ON) {
    case 'location':
      taxableAmount = baseAmount;
      break;
    case 'service_fee':
      taxableAmount = serviceFee;
      break;
    case 'both':
      taxableAmount = baseAmount + serviceFee;
      break;
    default:
      taxableAmount = serviceFee; // Par défaut sur frais de service
  }
  
  return {
    amount: (taxableAmount * taxPercent) / 100,
    percent: taxPercent,
    taxableAmount: taxableAmount,
    taxName: regionalConfig?.TAX_NAME || 'TVA'
  };
};

/**
 * Arrondi selon la configuration
 */
export const roundAmount = (amount, config = PRICING_CONFIG) => {
  if (!config.ROUND_TO_CENTS) return amount;
  
  switch (config.ROUND_METHOD) {
    case 'floor':
      return Math.floor(amount * 100) / 100;
    case 'ceil':
      return Math.ceil(amount * 100) / 100;
    case 'round':
    default:
      return Math.round(amount * 100) / 100;
  }
};

/**
 * Calcul de la caution
 */
export const calculateDeposit = (listing, baseAmount, config = PRICING_CONFIG) => {
  let depositAmount = 0;
  
  switch (config.DEPOSIT_CALCULATION) {
    case 'listing':
      depositAmount = parseFloat(listing.deposit_amount) || 0;
      break;
    case 'percentage':
      depositAmount = (baseAmount * config.DEFAULT_DEPOSIT_PERCENT) / 100;
      break;
    case 'fixed':
      depositAmount = config.MIN_DEPOSIT;
      break;
    default:
      depositAmount = parseFloat(listing.deposit_amount) || 0;
  }
  
  // Appliquer les limites
  if (depositAmount > 0) {
    depositAmount = Math.max(config.MIN_DEPOSIT, Math.min(config.MAX_DEPOSIT, depositAmount));
  }
  
  return depositAmount;
};

export default PRICING_CONFIG;
